import { useCallback, useMemo } from "react";
import { Button, Flex, Icon, Icons, type TableColumnType } from "@ir/ui";
import {
  MasterDataReasonCodeMapping,
  type IMasterDataReasonCodeMapping,
  type MasterDataReasonCodeMapping as MasterDataReasonCodeMappingEntity,
} from "@src/domain/entities/masterData";
import { Permissions } from "@src/domain/permissions";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import {
  MASTER_DATA_COLUMN_VERTICAL_ALIGN,
  MasterDataListTable,
  createMasterDataLinkedSelectionColumn,
  renderMasterDataTextCell,
} from "./masterDataTable.shared";

export interface MasterDataReasonCodeMappingRow extends IMasterDataReasonCodeMapping {
  key: string;
  isLinked: boolean;
}

function mapMappingsToRows(
  mappings: MasterDataReasonCodeMappingEntity[],
): MasterDataReasonCodeMappingRow[] {
  return mappings.map((entity) => ({
    ...MasterDataReasonCodeMapping.toJSON(entity),
    key: String(entity.mappingId),
    isLinked: false,
  }));
}

function buildColumns(
  onEdit: (entity: MasterDataReasonCodeMappingEntity) => void,
  mappingsById: Map<number, MasterDataReasonCodeMappingEntity>,
): TableColumnType<MasterDataReasonCodeMappingRow>[] {
  return [
    {
      title: "Purchaser",
      dataIndex: "purchaserName",
      key: "purchaserName",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 180,
      fixed: "left",
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "Reason Code",
      dataIndex: "reasonCode",
      key: "reasonCode",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 220,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "Reason Code Key",
      dataIndex: "reasonCodeKey",
      key: "reasonCodeKey",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 200,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "Deduction Type",
      dataIndex: "deductionTypeName",
      key: "deductionTypeName",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 200,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "Promo Type",
      dataIndex: "promoTypeName",
      key: "promoTypeName",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 180,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "Actions",
      dataIndex: "mappingId",
      key: "actions",
      align: "center",
      fixed: "right",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 80,
      cellRender: (value: unknown) => {
        const mapping = mappingsById.get(Number(value));
        if (!mapping) return null;
        return (
          <HasAnyPermission
            requiredPermissions={[Permissions.MASTER_DATA_EDIT]}
          >
            <Flex align="center" justifyContent="center">
              <Button
                size="small"
                variant="outline"
                color="grey"
                aria-label={`Edit ${mapping.reasonCodeKey}`}
                onPress={() => onEdit(mapping)}
              >
                <Icon name={Icons.Edit} size="small" />
              </Button>
            </Flex>
          </HasAnyPermission>
        );
      },
    },
  ];
}

interface MasterDataReasonCodeMappingsTableProps {
  reasonCodeMappings: MasterDataReasonCodeMappingEntity[];
  search: string;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;
  onEdit: (entity: MasterDataReasonCodeMappingEntity) => void;
  showSelection: boolean;
  selectedIds: ReadonlySet<number>;
  onSelectionChange: (selectedIds: Set<number>) => void;
}

export const MasterDataReasonCodeMappingsTable = ({
  reasonCodeMappings,
  search,
  setSearch,
  setPage,
  onEdit,
  showSelection,
  selectedIds,
  onSelectionChange,
}: MasterDataReasonCodeMappingsTableProps) => {
  const mappingsById = useMemo(() => {
    const map = new Map<number, MasterDataReasonCodeMappingEntity>();
    for (const entity of reasonCodeMappings) {
      map.set(entity.mappingId, entity);
    }
    return map;
  }, [reasonCodeMappings]);

  const dataSource = useMemo(
    () => mapMappingsToRows(reasonCodeMappings),
    [reasonCodeMappings],
  );

  const selectedRowKeys = useMemo(
    () => Array.from(selectedIds).map(String),
    [selectedIds],
  );

  const selectableKeys = useMemo(
    () => reasonCodeMappings.map((entity) => String(entity.mappingId)),
    [reasonCodeMappings],
  );

  const handleSelectionChange = useCallback(
    (keys: Set<string>) => {
      const next = new Set<number>();
      for (const key of keys) {
        const id = Number(key);
        if (mappingsById.has(id)) next.add(id);
      }
      onSelectionChange(next);
    },
    [mappingsById, onSelectionChange],
  );

  const columns = useMemo(() => {
    const dataColumns = buildColumns(onEdit, mappingsById);
    if (!showSelection) return dataColumns;
    return [
      createMasterDataLinkedSelectionColumn<MasterDataReasonCodeMappingRow>({
        selectedKeys: new Set(selectedRowKeys),
        selectableKeys,
        onChange: handleSelectionChange,
        getAriaLabel: (record) => `Select ${record.reasonCodeKey}`,
      }),
      ...dataColumns,
    ];
  }, [
    handleSelectionChange,
    mappingsById,
    onEdit,
    selectableKeys,
    selectedRowKeys,
    showSelection,
  ]);

  return (
    <MasterDataListTable
      columns={columns}
      dataSource={dataSource}
      search={search}
      entityLabel="reason codes"
      ariaLabel="Reason Code table"
      setSearch={setSearch}
      setPage={setPage}
    />
  );
};
